import { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuCheckCircle, LuXCircle } from 'react-icons/lu';
import { IoChevronDown } from 'react-icons/io5';
import type { ILateCheckoutValidateResult } from '../../types';

interface LateCheckoutTabProps {
  selectedTime: string;
  onTimeChange: (time: string) => void;
  currentCheckoutTime: string;
  isValidating: boolean;
  validationResult: ILateCheckoutValidateResult | null;
  isRtl: boolean;
}

const formatHourAr = (time24: string): string => {
  const hour = parseInt(time24.split(':')[0], 10);
  if (hour === 0) return '12:00 ص';
  if (hour < 12) return `${String(hour).padStart(2, '0')}:00 ص`;
  if (hour === 12) return '12:00 م (الظهر)';
  return `${String(hour - 12).padStart(2, '0')}:00 م`;
};

const formatHourEn = (time24: string): string => {
  const hour = parseInt(time24.split(':')[0], 10);
  if (hour === 0) return '12:00 AM';
  if (hour < 12) return `${String(hour).padStart(2, '0')}:00 AM`;
  if (hour === 12) return '12:00 PM (Noon)';
  return `${String(hour - 12).padStart(2, '0')}:00 PM`;
};

const LateCheckoutTab = ({ selectedTime, onTimeChange, currentCheckoutTime, isValidating, validationResult, isRtl }: LateCheckoutTabProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isValid = validationResult?.isValid === true;
  const isFailed = validationResult !== null && !validationResult.isValid;
  const message = validationResult
    ? (isAr ? validationResult.messageAr : validationResult.messageEn)
    : null;

  const borderColor = isFailed ? '#EF4444' : isValid ? '#22C55E' : '#E5E7EB';

  // Generate hourly options starting from the current checkout hour onwards (late = after current)
  const hourlyOptions = useMemo(() => {
    const parsed = currentCheckoutTime ? parseInt(currentCheckoutTime.split(':')[0], 10) : NaN;
    const checkoutHour = isNaN(parsed) || parsed === 0 ? 10 : parsed;
    const options: string[] = [];
    for (let h = checkoutHour; h <= 23; h++) {
      options.push(`${String(h).padStart(2, '0')}:00`);
    }
    return options;
  }, [currentCheckoutTime]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatHour = isAr ? formatHourAr : formatHourEn;
  const selectedLabel = selectedTime ? formatHour(selectedTime) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Typography
        sx={{
          fontFamily: 'Tajawal, sans-serif',
          fontWeight: 700,
          fontSize: '15px',
          color: '#435363',
          mb: '4px',
        }}
      >
        {t('TaskManagemnt.expectedCheckoutTime')}
      </Typography>

      {/* Dropdown */}
      <Box ref={dropdownRef} sx={{ position: 'relative' }}>
        <Box
          onClick={() => setDropdownOpen((prev) => !prev)}
          sx={{
            width: '100%',
            height: '49px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '12px',
            border: `1px solid ${borderColor}`,
            borderRadius: '12px',
            backgroundColor: '#fff',
            cursor: 'pointer',
            transition: 'border-color 0.15s ease',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '15px',
              fontWeight: 500,
              color: selectedTime ? '#261b6a' : '#8E98A1',
            }}
          >
            {selectedLabel || t('TaskManagemnt.selectTime')}
          </Typography>
          {isValidating ? (
            <CircularProgress size={20} sx={{ color: '#5B3FFF' }} />
          ) : (
            <IoChevronDown
              size={18}
              color="#5B3FFF"
              style={{
                transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease',
              }}
            />
          )}
        </Box>

        {/* Options list */}
        {dropdownOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: '54px',
              left: 0,
              right: 0,
              maxHeight: '240px',
              overflowY: 'auto',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '12px',
              boxShadow: '0px 4px 20px rgba(50,50,71,0.08)',
              zIndex: 20,
              py: '4px',
            }}
          >
            {hourlyOptions.map((time) => {
              const isCurrentCheckout = time === currentCheckoutTime?.substring(0, 5);
              const isSelected = time === selectedTime;
              return (
                <Box
                  key={time}
                  onClick={() => {
                    onTimeChange(time);
                    setDropdownOpen(false);
                  }}
                  sx={{
                    px: '16px',
                    py: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'flex-start',
                    backgroundColor: isSelected ? '#F3F0FF' : 'transparent',
                    '&:hover': { backgroundColor: '#F9F8FF' },
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '15px',
                      fontWeight: isCurrentCheckout || isSelected ? 600 : 400,
                      color: isCurrentCheckout || isSelected ? '#5B3FFF' : '#435363',
                    }}
                  >
                    {formatHour(time)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Validation feedback — success */}
      {!isValidating && isValid && selectedTime && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LuCheckCircle size={16} color="#22C55E" />
          <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#22C55E' }}>
            {message || t('TaskManagemnt.lateCheckoutAvailable')}
          </Typography>
        </Box>
      )}

      {/* Validation feedback — error */}
      {!isValidating && isFailed && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '4px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <LuXCircle size={16} color="#EF4444" style={{ flexShrink: 0 }} />
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#EF4444' }}>
              {message || t('TaskManagemnt.lateCheckoutUnavailable')}
            </Typography>
          </Box>

          {/* Conflict with next booking */}
          {validationResult.conflictsWithNextBooking && (
            <Box sx={{ backgroundColor: '#FEF2F2', borderRadius: '8px', p: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 600, color: '#991B1B' }}>
                {isAr ? 'يتعارض مع حجز قادم' : 'Conflicts with next booking'}
              </Typography>
              {validationResult.nextBookingCheckIn && (
                <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#7F1D1D' }}>
                  {isAr ? 'موعد دخول الحجز القادم: ' : 'Next check-in: '}
                  {new Date(validationResult.nextBookingCheckIn).toLocaleString(isAr ? 'ar-SA' : 'en-US', { dateStyle: 'short', timeStyle: 'short' })}
                </Typography>
              )}
              {validationResult.minutesUntilNextCheckIn !== null && (
                <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#7F1D1D' }}>
                  {isAr ? 'الوقت حتى الحجز القادم (بالدقائق): ' : 'Minutes until next check-in: '}
                  {validationResult.minutesUntilNextCheckIn}
                </Typography>
              )}
            </Box>
          )}

          {validationResult.impactsNextGuest && !validationResult.conflictsWithNextBooking && (
            <Box sx={{ backgroundColor: '#FFFBEB', borderRadius: '8px', p: '12px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', fontWeight: 600, color: '#92400E' }}>
                {isAr ? 'قد يؤثر على الضيف القادم' : 'May impact next guest'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default LateCheckoutTab;
